/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 *
 * @author Christian Blättler
 */

import {
  AbsoluteTime,
  assertUnreachable,
  Duration,
  HttpStatusCode,
  TalerMerchantApi,
  TalerProtocolDuration,
  TalerProtocolTimestamp,
} from "@gnu-taler/taler-util";
import {
  ButtonBetterBulma,
  LocalNotificationBannerBulma,
  undefinedIfEmpty,
  useLocalNotificationBetter,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { addDays, addMonths, endOfMonth, format, startOfMonth } from "date-fns";
import { h, VNode } from "preact";
import { useState } from "preact/hooks";
import {
  FormErrors,
  FormProvider,
} from "../../../../components/form/FormProvider.js";
import { InputDate } from "../../../../components/form/InputDate.js";
import { InputDurationSelector } from "../../../../components/form/InputDurationSelector.js";
import { InputSelector } from "../../../../components/form/InputSelector.js";
import { InputWithAddon } from "../../../../components/form/InputWithAddon.js";
import { useSessionContext } from "../../../../context/session.js";
import {
  dateFormatForPreferences,
  usePreference,
} from "../../../../hooks/preference.js";
import { Input } from "../../../../components/form/Input.js";
import { Tooltip } from "../../../../components/Tooltip.js";
import { InputDurationDropdown } from "../../../../components/form/InputDurationDropdown.js";

const TALER_SCREEN_ID = 67;

type Entity = TalerMerchantApi.TokenFamilyCreateRequest;

export interface Props {
  onCreated: () => void;
  onBack?: () => void;
}

const today = new Date();
const nextMonth = addMonths(today, 1);
const firstDayNextMonth = startOfMonth(nextMonth);
const lastDayNextMonth = endOfMonth(nextMonth);
const afterLastDayNextMonth = addDays(lastDayNextMonth, 1);
const firstDayNextMonthAnd30Days = addDays(firstDayNextMonth, 30);

const MINUTE = TalerProtocolDuration.fromSpec({minutes: 1})
const HOUR = TalerProtocolDuration.fromSpec({hours: 1})
const DAY = TalerProtocolDuration.fromSpec({days: 1})
const MONTH = TalerProtocolDuration.fromSpec({days: 30})
const THREE_MONTH = TalerProtocolDuration.fromSpec({days: 90})
const YEAR = TalerProtocolDuration.fromSpec({days: 365})

export function CreatePage({ onCreated, onBack }: Props): VNode {
  const { state: session, lib } = useSessionContext();
  const [notification, safeFunctionHandler] = useLocalNotificationBetter();
  const { i18n } = useTranslationContext();

  const [value, valueHandler] = useState<Partial<Entity>>({
    slug: undefined,
    name: undefined,
    description: undefined,
    description_i18n: {},
    kind: TalerMerchantApi.TokenFamilyKind.Discount,
    duration: TalerProtocolDuration.forever(),
    valid_after: TalerProtocolTimestamp.now(),
    valid_before: TalerProtocolTimestamp.never(),
    validity_granularity: undefined,
  });

  const errors = undefinedIfEmpty<FormErrors<Entity>>({
    slug: !value.slug ? i18n.str`Required` : undefined,
    name: !value.name ? i18n.str`Required` : undefined,
    description: !value.description ? i18n.str`Required` : undefined,
    valid_after:
      !value.valid_after || value.valid_after.t_s === "never"
        ? i18n.str`Required`
        : undefined,
    valid_before: !value.valid_before
      ? i18n.str`Required`
      : value.valid_after &&
          AbsoluteTime.cmp(
            AbsoluteTime.fromProtocolTimestamp(value.valid_after),
            AbsoluteTime.fromProtocolTimestamp(value.valid_before),
          ) > 0
        ? i18n.str`Expiration should be after start date`
        : undefined,
    duration: !value.duration ? i18n.str`Required` : undefined,
    validity_granularity: !value.validity_granularity
      ? i18n.str`Required`
      : value.duration &&
          Duration.cmp(
            Duration.fromTalerProtocolDuration(value.duration),
            Duration.fromTalerProtocolDuration(value.validity_granularity),
          ) < 1
        ? i18n.str`Granularity can't be greater than duration.`
        : undefined,
    kind: !value.kind ? i18n.str`Required` : undefined,
  });

  const create = safeFunctionHandler(
    i18n.str`create token family`,
    lib.instance.createTokenFamily.bind(lib.instance),
    !session.token || !!errors ? undefined : [session.token, value as Entity],
  );
  create.onSuccess = onCreated;
  create.onFail = (fail) => {
    switch (fail.case) {
      case HttpStatusCode.Unauthorized:
        return i18n.str`Unauthorized`;
      case HttpStatusCode.NotFound:
        return i18n.str`Not found`;
      case HttpStatusCode.Conflict:
        return i18n.str`There is another token family with this ID. Choose another one.`;
      default:
        assertUnreachable(fail);
    }
  };
  const [preferences] = usePreference();

  return (
    <div>
      <LocalNotificationBannerBulma notification={notification} />
      <section class="section is-main-section">
        <div class="columns">
          <div class="column" />
          <div class="column is-four-fifths">
            <FormProvider<Entity>
              name="token_family"
              errors={errors}
              object={value}
              valueHandler={valueHandler}
            >
              <InputWithAddon<Entity>
                name="slug"
                addonBefore={
                  new URL("tokenfamily/", session.backendUrl.href).href
                }
                label={i18n.str`Slug`}
                tooltip={i18n.str`Token family slug to use in URLs (for internal use only)`}
              />
              <Input<Entity>
                name="name"
                inputType="text"
                label={i18n.str`Name`}
                tooltip={i18n.str`User-readable token family name`}
              />
              <Input<Entity>
                name="description"
                inputType="multiline"
                label={i18n.str`Description`}
                tooltip={i18n.str`Token family description for customers`}
              />
              <InputSelector<Entity>
                name="kind"
                label={i18n.str`Kind`}
                tooltip={i18n.str`Choose between a discount and a subscription`}
                values={["discount", "subscription"]}
              />
              <InputDate<Entity>
                name="valid_after"
                label={i18n.str`Start Date`}
                tooltip={i18n.str`The first day the coupon/subscription can be used.`}
                help={i18n.str`If set to ${format(
                  firstDayNextMonth,
                  dateFormatForPreferences(preferences),
                )}, it cannot be used before this date.`}
                withNever
                withoutClear
                useProtocolTimestamp
              />

              <InputDate<Entity>
                name="valid_before"
                label={i18n.str`Expiration Date`}
                tooltip={i18n.str`The last day the coupon/subscription can be used.`}
                help={i18n.str`If set to ${format(
                  lastDayNextMonth,
                  dateFormatForPreferences(preferences),
                )}, they are no longer valid from ${format(
                  afterLastDayNextMonth,
                  dateFormatForPreferences(preferences),
                )}.`}
                withNever
                withoutClear
                useProtocolTimestamp
              />
              <InputDurationDropdown<Entity>
                name="duration"
                label={i18n.str`Duration`}
                tooltip={i18n.str`How long the coupon/subscription remains valid after being activated.`}
                help={i18n.str`If you activate it on ${format(
                  firstDayNextMonth,
                  dateFormatForPreferences(preferences),
                )} with duration of 30 days, it remains valid until ${format(
                  firstDayNextMonthAnd30Days,
                  dateFormatForPreferences(preferences),
                )}.`}
                withForever
                useProtocolDuration
              />

              <InputSelector
                name="validity_granularity"
                label={i18n.str`Validity Granularity`}
                tooltip={i18n.str`Rounds the validity to a specific unit of time.`}
                values={[undefined, "MINUTE", "HOUR", "DAY", "MONTH", "THREE_MONTH", "YEAR"]}
                toStr={(str) => {
                  if (str === "MINUTE") return i18n.str`1 minute`;
                  if (str === "HOUR") return i18n.str`1 hour`;
                  if (str === "DAY") return i18n.str`1 day`;
                  if (str === "MONTH") return i18n.str`30 days`;
                  if (str === "THREE_MONTH") return i18n.str`90 days`;
                  if (str === "YEAR") return i18n.str`365 days`;
                  return i18n.str`Select one`;
                }}
                fromStr={(str) => {
                  if (str === "MINUTE") return MINUTE;
                  if (str === "HOUR") return HOUR;
                  if (str === "DAY") return DAY;
                  if (str === "MONTH") return MONTH;
                  if (str === "THREE_MONTH") return THREE_MONTH;
                  if (str === "YEAR") return YEAR;
                  return undefined
                }}
              />

              <div class="buttons is-right mt-5">
                {onBack && (
                  <button type="button" class="button" onClick={onBack}>
                    <i18n.Translate>Cancel</i18n.Translate>
                  </button>
                )}
                <Tooltip
                  text={
                    !create.args
                      ? i18n.str`Please complete the marked fields`
                      : i18n.str`Confirm operation`
                  }
                >
                  <ButtonBetterBulma onClick={create} type="submit">
                    <i18n.Translate>Confirm</i18n.Translate>
                  </ButtonBetterBulma>
                </Tooltip>
              </div>
            </FormProvider>
          </div>
          <div class="column" />
        </div>
      </section>
    </div>
  );
}
